import {
  MockHook,
  useAccounts,
  useSettings,
  useSnackbarNotification,
} from '@jupiterone/jupiter-web-toolkit';
import { useState } from 'react';
import asArray from './asArray';

const isNil = (value: any): value is undefined => value === undefined;

export const QUERY_PERMISSIONS_CATEGORY = 'query-policy';

export type QueryPermissions = {
  [permissionType: string]: string[];
};

export type QueryPermissionsProps = {
  groupId: string;
};

export type AccountSettingsQueryPolicy = {
  filtersByGroupId: {
    [groupId: string]: QueryPermissions[] | QueryPermissions;
  };
} | null;

type AccountSettingsQueryPolicyStrict = {
  filtersByGroupId: {
    [groupId: string]: QueryPermissions[];
  };
};

type UpdateQueryPermissionsForGroup = (
  newPermissions: QueryPermissions[]
) => Promise<AccountSettingsQueryPolicyStrict>;

const useQueryPermissionsLogic = ({ groupId }: QueryPermissionsProps) => {
  const [editIndex, setEditIndex] = useState<number>();
  const [deleteIndex, setDeleteIndex] = useState<number>();
  const [saving, setSaving] = useState(false);
  const notification = useSnackbarNotification();
  const { account } = useAccounts();
  const [
    allPermissions,
    setAllPermissions,
  ] = useSettings<AccountSettingsQueryPolicy>(
    'account',
    QUERY_PERMISSIONS_CATEGORY,
    account?.id
  );

  const permissionSetsSource = asArray(
    allPermissions.data?.filtersByGroupId[groupId]
  );

  const permissionSets = permissionSetsSource.map((permissionSet, index) => ({
    title: `Permission Set #${index + 1}`,
    entries: Object.entries(permissionSet),
    handleEditPermissionSet: () => setEditIndex(index),
    handleDeletePermissionSet: () => setDeleteIndex(index),
  }));

  const editing = !isNil(editIndex)
    ? {
        title: `Editing permission set #${editIndex + 1}`,
        permissionSet:
          permissionSetsSource[editIndex] ?? ({} as QueryPermissions),
      }
    : undefined;

  const deleting = !isNil(deleteIndex)
    ? {
        title: `Delete permission set #${deleteIndex + 1}`,
      }
    : undefined;

  const handleEditCancel = () => setEditIndex(undefined);
  const handleDeleteCancel = () => setDeleteIndex(undefined);

  const updateQueryPermissionsForGroup: UpdateQueryPermissionsForGroup = async (
    newPermissionsForGroup: QueryPermissions[]
  ) => {
    const newPermissions: AccountSettingsQueryPolicyStrict = {
      filtersByGroupId: {
        [groupId]: newPermissionsForGroup,
      },
    };
    await setAllPermissions(newPermissions);
    return newPermissions;
  };

  const handleAddNewPermissionSet = () =>
    setEditIndex(permissionSetsSource.length);

  const handlePermissionUpsert = async (newPermission: QueryPermissions) => {
    setSaving(true);
    const newPermissions = [...permissionSetsSource];
    if (isNil(editIndex)) {
      newPermissions.push(newPermission);
    } else {
      newPermissions[editIndex] = newPermission;
    }

    try {
      await updateQueryPermissionsForGroup(newPermissions);
      setEditIndex(undefined);
      const num = isNil(editIndex) ? newPermissions.length : editIndex + 1;
      notification.success(`Permission Set ${num} saved`, 3000);
      return newPermissions;
    } catch (error) {
      notification.error({ message: error.message, error });
    } finally {
      setSaving(false);
    }
  };

  const handlePermissionDelete = async () => {
    if (!isNil(deleteIndex)) {
      setSaving(true);
      const newPermissions = permissionSetsSource.filter(
        (_p, i) => i !== deleteIndex
      );
      try {
        await updateQueryPermissionsForGroup(newPermissions);
        notification.success(`Permission Set #${deleteIndex} deleted`, 3000);
        setDeleteIndex(undefined);
        return newPermissions;
      } catch (error) {
        notification.error({ message: error.message, error });
      } finally {
        setSaving(false);
      }
    }
  };

  return {
    permissionSets,
    loading: allPermissions.loading,
    saving,
    editing,
    deleting,
    handleAddNewPermissionSet,
    handlePermissionUpsert,
    handlePermissionDelete,
    handleEditCancel,
    handleDeleteCancel,
  };
};

export type QueryPermissionsComponentProps = ReturnType<
  typeof useQueryPermissionsLogic
>;
export const MOCK_QUERY_PERMISSIONS_PROPS: QueryPermissionsComponentProps = {
  permissionSets: [
    {
      title: 'Permission set #0',
      entries: [['_integrationType', ['abc', 'def']]],
      handleEditPermissionSet: MockHook.mockFunction,
      handleDeletePermissionSet: MockHook.mockFunction,
    },
  ],
  loading: false,
  saving: false,
  editing: undefined,
  deleting: undefined,
  handleAddNewPermissionSet: MockHook.mockFunction,
  handlePermissionUpsert: MockHook.mockAsyncFunction,
  handlePermissionDelete: MockHook.mockAsyncFunction,
  handleEditCancel: MockHook.mockFunction,
  handleDeleteCancel: MockHook.mockFunction,
};
export default MockHook.useMockHook(
  useQueryPermissionsLogic,
  MOCK_QUERY_PERMISSIONS_PROPS
);
